import { useCallback, useRef } from 'react';

/**
 * Hook that throttles scroll events to improve performance
 * @param callback - Function to call on scroll
 * @param delay - Throttle delay in milliseconds (default: 100ms)
 * @returns Throttled callback function
 */
export const useThrottledScroll = <T extends (...args: never[]) => unknown>(
  callback: T,
  delay = 100
): T => {
  const throttleRef = useRef<NodeJS.Timeout | null>(null);
  const lastCallRef = useRef<number>(0);

  const throttledCallback = useCallback((...args: Parameters<T>) => {
    const now = Date.now();
    
    // If enough time has passed since last call, execute immediately
    if (now - lastCallRef.current >= delay) {
      lastCallRef.current = now;
      callback(...args);
    } else {
      // Otherwise, schedule for later
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
      }
      
      throttleRef.current = setTimeout(() => {
        lastCallRef.current = Date.now();
        callback(...args);
      }, delay - (now - lastCallRef.current));
    }
  }, [callback, delay]) as T;

  return throttledCallback;
};